import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

export default function HeroCarousel({ items }: { items: Movie[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const movie = items[active];

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[560px] md:h-[640px] overflow-hidden">
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            idx === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/60" />

      <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 h-full flex flex-col justify-center pb-16 md:pb-20">
        <div key={movie.id} className="max-w-2xl animate-fade-in-up">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-600 text-background-50">
              编辑推荐
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs bg-background-900/80 text-foreground-200 backdrop-blur-sm border border-background-700">
              {movie.type} · {movie.region} · {movie.year}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-50 leading-tight text-balance">
            {movie.title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-foreground-300 leading-relaxed line-clamp-2-custom max-w-xl">
            {movie.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${movie.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={`/category?type=${encodeURIComponent(movie.type)}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-900/80 backdrop-blur border border-background-700 hover:border-accent-500 text-foreground-100 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              更多{movie.type}
            </Link>
          </div>
          <div className="mt-5 flex items-center gap-4 text-xs text-foreground-400">
            <span className="flex items-center gap-1 text-accent-400">
              <i className="ri-star-fill"></i>
              {movie.rating} 评分
            </span>
            <span>{movie.genres.join(" · ")}</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-6 z-10 flex items-center gap-3">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="上一张"
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-900/80 backdrop-blur border border-background-700 text-foreground-100 hover:border-accent-500 hover:text-accent-400 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="下一张"
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-900/80 backdrop-blur border border-background-700 text-foreground-100 hover:border-accent-500 hover:text-accent-400 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>

      <div className="absolute bottom-6 left-4 md:left-6 z-10 flex items-center gap-2">
        {items.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setActive(idx)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              idx === active ? "w-8 bg-accent-500" : "w-3 bg-background-600 hover:bg-background-400"
            }`}
          />
        ))}
      </div>
    </section>
  );
}